import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { getConfig } from "../lib/api";
import { useSiteClub } from "../lib/SiteClubContext";
import { startFacebookAuthorize } from "../lib/facebookAuth";
import { normalizePhone } from "../lib/phone";
import InfoTip from "../components/InfoTip";
import { NO_REMINDERS_LABEL, PHONE_TIP } from "../lib/notifications";

// The sign-up text-reminder choice has to survive until the account is
// confirmed (ConfirmSignUp is what hands it to the backend), and people
// often confirm later from the "Already have a code?" link. Keyed by email
// so a different sign-up in the same browser can't pick it up. If it's
// missing at confirm time, the backend treats reminders as off.
const prefsKey = (email: string) => `rcracehq.signup.smsRaceReminders.${email.trim().toLowerCase()}`;

type View = "login" | "signup" | "confirm" | "forgot" | "reset";

export default function LoginPage() {
  const { signIn, signUp, confirmSignUp, resendConfirmationCode, forgotPassword, confirmForgotPassword } =
    useAuth();
  const navigate = useNavigate();

  const [view, setView] = useState<View>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [phone, setPhone] = useState("");
  const [noReminders, setNoReminders] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [facebookEnabled, setFacebookEnabled] = useState(false);
  // A club can turn Facebook sign-in off for its own site (Manage ->
  // Advanced settings); email sign-in always stays.
  const { club } = useSiteClub();
  const showFacebook = facebookEnabled && club?.facebookSignIn !== false;

  // GET /config decides whether the button shows at all -- a stage with no
  // Facebook App configured yet (FacebookClientId left blank) never gets a
  // button that would dead-end on a Cognito error.
  useEffect(() => {
    getConfig()
      .then((cfg) => setFacebookEnabled(!!cfg.facebookEnabled))
      .catch(() => setFacebookEnabled(false));
  }, []);

  async function handleFacebookLogin() {
    setError(null);
    try {
      await startFacebookAuthorize();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start Facebook sign-in.");
    }
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(email, password);
      navigate("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign in failed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    const e164 = normalizePhone(phone);
    if (!e164) {
      setError("Enter a valid phone number, e.g. (509) 555-1234.");
      return;
    }
    setBusy(true);
    try {
      await signUp(email, password, e164);
      localStorage.setItem(prefsKey(email), String(!noReminders));
      setInfo("Check your email for a confirmation code.");
      setView("confirm");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign up failed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirm(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const smsRaceReminders = localStorage.getItem(prefsKey(email));
      await confirmSignUp(email, code, smsRaceReminders ? { smsRaceReminders } : undefined);
      localStorage.removeItem(prefsKey(email));
      setInfo("Account confirmed. You can sign in now.");
      setPassword("");
      setView("login");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Confirmation failed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleResend() {
    setError(null);
    setInfo(null);
    try {
      await resendConfirmationCode(email);
      setInfo("A new code has been sent.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not resend code.");
    }
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await forgotPassword(email);
      setInfo("Check your email for a password reset code.");
      setView("reset");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send reset code.");
    } finally {
      setBusy(false);
    }
  }

  async function handleResetPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await confirmForgotPassword(email, code, password);
      setInfo("Password reset. You can sign in now.");
      setPassword("");
      setConfirmPassword("");
      setCode("");
      setView("login");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Password reset failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ maxWidth: 420, margin: "3rem auto" }}>
      {view === "login" && (
        <form onSubmit={handleLogin}>
          <h2>Sign in</h2>
          {showFacebook && (
            <>
              <button
                type="button"
                className="secondary"
                style={{ width: "100%", marginBottom: "1rem" }}
                onClick={handleFacebookLogin}
              >
                Sign in with Facebook
              </button>
              <p className="muted" style={{ textAlign: "center", fontSize: 13, margin: "0 0 1rem" }}>or</p>
            </>
          )}
          <label htmlFor="login-email">Email</label>
          <input
            id="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{ marginBottom: "0.75rem" }}
          />
          <label htmlFor="login-password">Password</label>
          <input
            id="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={{ marginBottom: "1rem" }}
          />
          {error && <p className="err">{error}</p>}
          <button type="submit" disabled={busy} style={{ width: "100%" }}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <p className="muted" style={{ marginTop: "1rem", fontSize: 13 }}>
            New here?{" "}
            <button
              type="button"
              className="link"
              onClick={() => {
                setError(null);
                setInfo(null);
                setView("signup");
              }}
            >
              Create an account
            </button>
          </p>
          <p className="muted" style={{ fontSize: 13 }}>
            Already have a code?{" "}
            <button
              type="button"
              className="link"
              onClick={() => {
                setError(null);
                setInfo(null);
                setView("confirm");
              }}
            >
              Confirm sign-up
            </button>
          </p>
          <p className="muted" style={{ fontSize: 13 }}>
            Forgot your password?{" "}
            <button
              type="button"
              className="link"
              onClick={() => {
                setError(null);
                setInfo(null);
                setView("forgot");
              }}
            >
              Reset it
            </button>
          </p>
        </form>
      )}

      {view === "signup" && (
        <form onSubmit={handleSignUp}>
          <h2>Create an account</h2>
          <p className="muted" style={{ fontSize: 13 }}>
            A club admin will need to verify your account before you can register for events or
            upload photos/videos.
          </p>
          <label htmlFor="signup-email">Email</label>
          <input
            id="signup-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{ marginBottom: "0.75rem" }}
          />
          <label htmlFor="signup-phone" style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
            Phone number <InfoTip text={PHONE_TIP} />
          </label>
          <input
            id="signup-phone"
            type="tel"
            autoComplete="tel"
            placeholder="(509) 555-1234"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
            style={{ marginBottom: "0.5rem" }}
          />
          <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.75rem" }}>
            <input
              type="checkbox"
              style={{ width: "auto" }}
              checked={noReminders}
              onChange={(e) => setNoReminders(e.target.checked)}
            />
            {NO_REMINDERS_LABEL}
          </label>
          <label htmlFor="signup-password">Password</label>
          <input
            id="signup-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={{ marginBottom: "0.75rem" }}
          />
          <label htmlFor="signup-confirm">Confirm password</label>
          <input
            id="signup-confirm"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            style={{ marginBottom: "1rem" }}
          />
          {error && <p className="err">{error}</p>}
          <button type="submit" disabled={busy} style={{ width: "100%" }}>
            {busy ? "Creating account…" : "Create account"}
          </button>
          <p className="muted" style={{ marginTop: "1rem", fontSize: 13 }}>
            <button type="button" className="link" onClick={() => setView("login")}>
              Back to sign in
            </button>
          </p>
        </form>
      )}

      {view === "confirm" && (
        <form onSubmit={handleConfirm}>
          <h2>Confirm your email</h2>
          <p className="muted" style={{ fontSize: 13 }}>
            Enter the one-time code emailed to you after sign-up.
          </p>
          <label htmlFor="confirm-email">Email</label>
          <input
            id="confirm-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{ marginBottom: "0.75rem" }}
          />
          <label htmlFor="confirm-code">Confirmation code</label>
          <input
            id="confirm-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            style={{ marginBottom: "1rem" }}
          />
          {error && <p className="err">{error}</p>}
          {info && <p className="ok">{info}</p>}
          <button type="submit" disabled={busy} style={{ width: "100%" }}>
            {busy ? "Confirming…" : "Confirm"}
          </button>
          <p className="muted" style={{ marginTop: "1rem", fontSize: 13 }}>
            <button type="button" className="link" onClick={handleResend}>
              Resend code
            </button>
            {" · "}
            <button type="button" className="link" onClick={() => setView("login")}>
              Back to sign in
            </button>
          </p>
        </form>
      )}

      {view === "forgot" && (
        <form onSubmit={handleForgotPassword}>
          <h2>Reset your password</h2>
          <p className="muted" style={{ fontSize: 13 }}>
            We'll email you a code to reset your password.
          </p>
          <label htmlFor="forgot-email">Email</label>
          <input
            id="forgot-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{ marginBottom: "1rem" }}
          />
          {error && <p className="err">{error}</p>}
          <button type="submit" disabled={busy} style={{ width: "100%" }}>
            {busy ? "Sending code…" : "Send reset code"}
          </button>
          <p className="muted" style={{ marginTop: "1rem", fontSize: 13 }}>
            Already have a code?{" "}
            <button type="button" className="link" onClick={() => setView("reset")}>
              Enter it
            </button>
            {" · "}
            <button type="button" className="link" onClick={() => setView("login")}>
              Back to sign in
            </button>
          </p>
        </form>
      )}

      {view === "reset" && (
        <form onSubmit={handleResetPassword}>
          <h2>Enter reset code</h2>
          <label htmlFor="reset-email">Email</label>
          <input
            id="reset-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{ marginBottom: "0.75rem" }}
          />
          <label htmlFor="reset-code">Reset code</label>
          <input
            id="reset-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            style={{ marginBottom: "0.75rem" }}
          />
          <label htmlFor="reset-password">New password</label>
          <input
            id="reset-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={{ marginBottom: "0.75rem" }}
          />
          <label htmlFor="reset-confirm">Confirm new password</label>
          <input
            id="reset-confirm"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            style={{ marginBottom: "1rem" }}
          />
          {error && <p className="err">{error}</p>}
          {info && <p className="ok">{info}</p>}
          <button type="submit" disabled={busy} style={{ width: "100%" }}>
            {busy ? "Resetting…" : "Reset password"}
          </button>
          <p className="muted" style={{ marginTop: "1rem", fontSize: 13 }}>
            <button type="button" className="link" onClick={handleForgotPassword}>
              Resend code
            </button>
            {" · "}
            <button type="button" className="link" onClick={() => setView("login")}>
              Back to sign in
            </button>
          </p>
        </form>
      )}
    </div>
  );
}
